<script setup lang="ts">
// PurchaseFlowGuide.vue - 代購流程說明組件
// 純靜態展示：用戶必讀、中國段流程、國際配送
import { $t } from "@user-center/utils/i18n";

function _t(key: string, fallback: string): string {
  const result = $t(key);
  return result && result !== key ? result : fallback;
}

import readIcon from '@/assets/read-icon.png'
import descriptionText from '@/assets/Description_text.png'
import firstPay from '@/assets/first_pay.png'
import secondFh from '@/assets/second_fh.png'
import thirdPay from '@/assets/third_paypng.png'
import truck from '@/assets/truck.png'
import sourceCheck from '@/assets/source_check.png'
import consult from '@/assets/consult.png'
import internalExpress from '@/assets/internal_express.png'
import safePay from '@/assets/safe_pay.png'
import fourpackage from '@/assets/fourth_package.png'
</script>

<template>
  <div class="purchase-flow-guide">
    <!-- 禁運物品 - 後台配置預留區 -->
    <!-- <div class="purchase-flow-guide__restricted">
      <div class="purchase-flow-guide__restricted-bg" />
      <p class="purchase-flow-guide__restricted-text">
        禁運物品-後台配置
      </p>
    </div> -->

    <!-- 主流程容器 -->
    <div class="purchase-flow-guide__main">
      <!-- 左側：用戶必讀 -->
      <div class="purchase-flow-guide__left">
        <div class="purchase-flow-guide__left-header">
          <div class="purchase-flow-guide__left-header-icon">
            <img :src="readIcon" alt="" width="20" height="20" />
          </div>
          <span class="purchase-flow-guide__left-header-text">{{ _t("purchaseFlow.userMustRead", "用戶必讀") }}</span>
        </div>
        <div class="purchase-flow-guide__left-body">
          <div class="purchase-flow-guide__left-title">
            <span>{{ _t("purchaseFlow.purchaseProcess", "代購流程") }}</span>
            <span class="purchase-flow-guide__left-title--highlight">{{ _t("purchaseFlow.explanation", "說明") }}</span>
          </div>
          <p class="purchase-flow-guide__left-subtitle">
            {{ _t("purchaseFlow.subtitle", "清楚了解流程，購買更安心") }}
          </p>
          <div class="purchase-flow-guide__left-desc">
            <img :src="descriptionText" alt="" class="purchase-flow-guide__left-desc-img" />
          </div>
        </div>
      </div>

      <!-- 中間：中國段流程 -->
      <div class="purchase-flow-guide__center">
        <div class="purchase-flow-guide__center-header">
          <div class="purchase-flow-guide__center-header-left">
            <div class="purchase-flow-guide__center-flag">
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                <rect width="16" height="16" rx="8" fill="#DE2910" />
                <path d="M4 3l1.5 1.5L4 7l2-1.5L8 7V3L6 4.5 4 3z" fill="#FFDE00" />
                <path d="M4 3l1.5 1.5L4 7" fill="#FFDE00" />
              </svg>
            </div>
            <span class="purchase-flow-guide__center-header-title">{{ _t("purchaseFlow.chinaSegment", "中國段流程") }}</span>
            <div class="purchase-flow-guide__center-header-divider" />
            <span class="purchase-flow-guide__center-header-step">{{ _t("purchaseFlow.steps1to4", "1~4步") }}</span>
          </div>
        </div>

        <div class="purchase-flow-guide__center-steps">
          <!-- Step 1 -->
          <div class="purchase-flow-guide__step-card">
            <div class="purchase-flow-guide__step-badge">1</div>
            <div class="purchase-flow-guide__step-icon">
              <img :src="firstPay" alt="" width="39" height="39" />
            </div>
            <div class="purchase-flow-guide__step-info">
              <p class="purchase-flow-guide__step-title">{{ _t("purchaseFlow.step1Title", "商品支付") }}</p>
              <p class="purchase-flow-guide__step-desc">
                {{ _t("purchaseFlow.step1Desc", "系統自動完成採購") }}<br />{{ _t("purchaseFlow.step1DescNote", "(人工代購除外)") }}
              </p>
            </div>
          </div>

          <div class="purchase-flow-guide__step-arrow">
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
              <path d="M6 4l4 4-4 4" stroke="#ffcea8" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" />
            </svg>
          </div>

          <!-- Step 2 -->
          <div class="purchase-flow-guide__step-card">
            <div class="purchase-flow-guide__step-badge">2</div>
            <div class="purchase-flow-guide__step-icon">
              <img :src="secondFh" alt="" width="39" height="39" />
            </div>
            <div class="purchase-flow-guide__step-info">
              <p class="purchase-flow-guide__step-title">{{ _t("purchaseFlow.step2Title", "商家發貨") }}</p>
              <p class="purchase-flow-guide__step-desc">
                {{ _t("purchaseFlow.step2Desc", "發往中轉倉") }}
              </p>
            </div>
          </div>

          <div class="purchase-flow-guide__step-arrow">
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
              <path d="M6 4l4 4-4 4" stroke="#ffcea8" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" />
            </svg>
          </div>

          <!-- Step 3 -->
          <div class="purchase-flow-guide__step-card">
            <div class="purchase-flow-guide__step-badge">3</div>
            <div class="purchase-flow-guide__step-icon">
              <img :src="thirdPay" alt="" width="39" height="39" />
            </div>
            <div class="purchase-flow-guide__step-info">
              <p class="purchase-flow-guide__step-title">{{ _t("purchaseFlow.step3Title", "倉庫簽收入庫") }}</p>
              <p class="purchase-flow-guide__step-desc">
                {{ _t("purchaseFlow.step3Desc", "同步向賣家確認收貨") }}
              </p>
            </div>
          </div>

          <div class="purchase-flow-guide__step-arrow">
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
              <path d="M6 4l4 4-4 4" stroke="#ffcea8" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" />
            </svg>
          </div>

          <!-- Step 4 -->
          <div class="purchase-flow-guide__step-card">
            <div class="purchase-flow-guide__step-badge">4</div>
            <div class="purchase-flow-guide__step-icon">
              <img :src="fourpackage" alt="" width="39" height="39" />
            </div>
            <div class="purchase-flow-guide__step-info">
              <p class="purchase-flow-guide__step-title">{{ _t("purchaseFlow.step4Title", "提交包裹") }}</p>
              <p class="purchase-flow-guide__step-desc">
                {{ _t("purchaseFlow.step4Desc", "商品合箱打包") }}
              </p>
            </div>
          </div>
        </div>

        <!-- 底部功能亮點 -->
        <div class="purchase-flow-guide__center-bottom">
          <div class="purchase-flow-guide__features">
            <div class="purchase-flow-guide__feature-item">
              <div class="purchase-flow-guide__feature-icon">
                <img :src="sourceCheck" alt="" width="48" height="48" />
              </div>
              <div class="purchase-flow-guide__feature-text">
                <p class="purchase-flow-guide__feature-name">{{ _t("purchaseFlow.featureSourceTitle", "來源透明") }}</p>
                <p class="purchase-flow-guide__feature-desc">{{ _t("purchaseFlow.featureSourceDesc", "正品來源可追溯") }}<br />{{ _t("purchaseFlow.featureSourceDesc2", "購物更安心") }}</p>
              </div>
            </div>
            <div class="purchase-flow-guide__feature-item">
              <div class="purchase-flow-guide__feature-icon">
                <img :src="consult" alt="" width="48" height="48" />
              </div>
              <div class="purchase-flow-guide__feature-text">
                <p class="purchase-flow-guide__feature-name">{{ _t("purchaseFlow.featureServiceTitle", "客服協助") }}</p>
                <p class="purchase-flow-guide__feature-desc">{{ _t("purchaseFlow.featureServiceDesc", "專人問題諮詢") }}<br />{{ _t("purchaseFlow.featureServiceDesc2", "全流程貼心協助") }}</p>
              </div>
            </div>
            <div class="purchase-flow-guide__feature-item">
              <div class="purchase-flow-guide__feature-icon">
                <img :src="truck" alt="" width="48" height="48" />
              </div>
              <div class="purchase-flow-guide__feature-text">
                <p class="purchase-flow-guide__feature-name">{{ _t("purchaseFlow.featureLogisticsTitle", "物流可追蹤") }}</p>
                <p class="purchase-flow-guide__feature-desc">{{ _t("purchaseFlow.featureLogisticsDesc", "國際物流全程追蹤") }}<br />{{ _t("purchaseFlow.featureLogisticsDesc2", "掌握配送進度") }}</p>
              </div>
            </div>
            <div class="purchase-flow-guide__feature-item">
              <div class="purchase-flow-guide__feature-icon">
                <img :src="safePay" alt="" width="48" height="48" />
              </div>
              <div class="purchase-flow-guide__feature-text">
                <p class="purchase-flow-guide__feature-name">{{ _t("purchaseFlow.featurePaymentTitle", "安全支付") }}</p>
                <p class="purchase-flow-guide__feature-desc">{{ _t("purchaseFlow.featurePaymentDesc", "多重加密保障") }}<br />{{ _t("purchaseFlow.featurePaymentDesc2", "交易更安心") }}</p>
              </div>
            </div>
          </div>
          <button class="purchase-flow-guide__detail-btn">
            <span>{{ _t("purchaseFlow.viewDetails", "查看詳細流程") }}</span>
            <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
              <path d="M5 3l4 4-4 4" stroke="white" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" />
            </svg>
          </button>
        </div>
      </div>

      <!-- 右側：國際配送 -->
      <div class="purchase-flow-guide__right">
        <div class="purchase-flow-guide__right-header">
          <div class="purchase-flow-guide__right-header-left">
            <div class="purchase-flow-guide__right-globe">
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                <circle cx="8" cy="8" r="6.5" stroke="#15803c" stroke-width="1.5" />
                <ellipse cx="8" cy="8" rx="3" ry="6.5" stroke="#15803c" stroke-width="1.5" />
                <path d="M2 8h12" stroke="#15803c" stroke-width="1.5" />
              </svg>
            </div>
            <span class="purchase-flow-guide__right-header-title">{{ _t("purchaseFlow.internationalDelivery", "國際配送") }}</span>
            <div class="purchase-flow-guide__right-header-divider" />
            <span class="purchase-flow-guide__right-header-step">{{ _t("purchaseFlow.step5", "第5步") }}</span>
          </div>
        </div>

        <div class="purchase-flow-guide__right-step">
          <div class="purchase-flow-guide__step-card purchase-flow-guide__step-card--green">
            <div class="purchase-flow-guide__step-badge purchase-flow-guide__step-badge--green">5</div>
            <div class="purchase-flow-guide__step-icon purchase-flow-guide__step-icon--green">
              <img :src="internalExpress" alt="" width="39" height="39" />
            </div>
            <div class="purchase-flow-guide__step-info">
              <p class="purchase-flow-guide__step-title">{{ _t("purchaseFlow.step5Title", "支付國際物流運費") }}</p>
              <p class="purchase-flow-guide__step-desc">
                {{ _t("purchaseFlow.step5Desc", "包裹發往目的地") }}
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- 幫助中心常見問題 (Figma design) -->
    <div class="purchase-flow-guide__help">
      <div class="purchase-flow-guide__help-column">
        <h3 class="purchase-flow-guide__help-title">{{ _t("purchaseFlow.inquireLogistics", "查詢物流") }}</h3>
        <div class="purchase-flow-guide__help-links">
          <p class="purchase-flow-guide__help-text">{{ _t("purchaseFlow.packageReceivingNotes", "包裹收貨注意事項") }}</p>
          <p class="purchase-flow-guide__help-text">{{ _t("purchaseFlow.undeliveredMeaning", "\"未妥投\"代表什麼意思？") }}</p>
          <p class="purchase-flow-guide__help-text">{{ _t("purchaseFlow.howToCheckLogistics", "包裹發出後，如何查詢物流信息？") }}</p>
          <p class="purchase-flow-guide__help-text">{{ _t("purchaseFlow.howToTrackLogistics", "包裹發出後，如何跟蹤物流信息？") }}</p>
        </div>
      </div>
      <div class="purchase-flow-guide__help-column">
        <h3 class="purchase-flow-guide__help-title">{{ _t("purchaseFlow.productAbnormal", "商品異常") }}</h3>
        <div class="purchase-flow-guide__help-links">
          <p class="purchase-flow-guide__help-text">{{ _t("purchaseFlow.aboutPackaging", "關於包裝細節") }}</p>
          <p class="purchase-flow-guide__help-text">{{ _t("purchaseFlow.productIssueAfterReceive", "收到貨後發現商品問題怎麼辦？") }}</p>
          <p class="purchase-flow-guide__help-text">{{ _t("purchaseFlow.fragileItemDamaged", "我代購的易碎品如果損壞怎麼辦？") }}</p>
          <p class="purchase-flow-guide__help-text">{{ _t("purchaseFlow.undeliveredMeaning2", "\"未妥投\"代表什麼意思？") }}</p>
        </div>
      </div>
      <div class="purchase-flow-guide__help-column">
        <h3 class="purchase-flow-guide__help-title">{{ _t("purchaseFlow.packageLost", "包裹丟失") }}</h3>
        <div class="purchase-flow-guide__help-links">
          <p class="purchase-flow-guide__help-text">{{ _t("purchaseFlow.packageInsurance", "包裹保險及賠償") }}</p>
          <p class="purchase-flow-guide__help-text">{{ _t("purchaseFlow.inspectionRules", "代購訂單標準驗貨規則") }}</p>
          <p class="purchase-flow-guide__help-text">{{ _t("purchaseFlow.stockIssue", "購物網上有貨，但為什麼說無貨呢？") }}</p>
        </div>
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.purchase-flow-guide {
  color: var(--text, #1a1817);
  display: flex;
  flex-direction: column;
  gap: 12px;
  margin-top: 24px;
}

// 禁運物品區
.purchase-flow-guide__restricted {
  background: #fff;
  border-radius: 12px;
  height: 200px;
  overflow: hidden;
  position: relative;
  width: 100%;
}

.purchase-flow-guide__restricted-bg {
  background: #d9d9d9;
  height: 160px;
  left: 50%;
  opacity: 0.52;
  position: absolute;
  top: 50%;
  transform: translate(-50%, -50%);
  width: calc(100% - 80px);
}

.purchase-flow-guide__restricted-text {
  color: #1a1817;
  font-family: 'Noto Sans TC', sans-serif;
  font-size: 40px;
  font-weight: 600;
  left: 50%;
  line-height: 48px;
  opacity: 0.5;
  position: absolute;
  text-align: center;
  top: 50%;
  transform: translate(-50%, -50%);
  white-space: nowrap;
}

// 主容器
.purchase-flow-guide__main {
  background: #fff;
  border-radius: 12px;
  display: flex;
  padding: 20px;
}

// ======= 左側：用戶必讀 =======
.purchase-flow-guide__left {
  background: linear-gradient(154deg, #fff 40.8%, #fff5ed 89.8%);
  border-radius: 12px;
  display: flex;
  flex-direction: column;
  flex-shrink: 0;
  overflow: hidden;
  position: relative;
  width: 288px;
}

.purchase-flow-guide__left-header {
  align-items: center;
  background: #ffe8d4;
  border-radius: 0 0 16px 16px;
  display: flex;
  gap: 4px;
  justify-content: center;
  left: 50%;
  padding: 8px 0;
  position: absolute;
  top: 0;
  transform: translateX(-50%);
  width: 200px;
}

.purchase-flow-guide__left-header-icon {
  align-items: center;
  display: flex;
  height: 20px;
  justify-content: center;
  width: 20px;
}

.purchase-flow-guide__left-header-text {
  font-size: 16px;
  font-weight: 600;
  line-height: 24px;
}

.purchase-flow-guide__left-body {
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding: 52px 20px 20px;
}

.purchase-flow-guide__left-title {
  font-size: 24px;
  font-weight: 600;
  line-height: 32px;
}

.purchase-flow-guide__left-title--highlight {
  color: #ff7228;
}

.purchase-flow-guide__left-subtitle {
  color: #645a56;
  font-size: 14px;
  line-height: 22px;
  margin: 0;
}

.purchase-flow-guide__left-desc {
  margin-top: 16px;
}

.purchase-flow-guide__left-desc-img {
  height: auto;
  width: 100%;
}

.purchase-flow-guide__left-desc-item {
  align-items: center;
  display: flex;
  gap: 8px;
}

.purchase-flow-guide__left-desc-icon {
  align-items: center;
  display: flex;
  flex-shrink: 0;
  height: 20px;
  justify-content: center;
  width: 20px;
}

.purchase-flow-guide__left-desc-item span {
  color: #645a56;
  font-size: 14px;
  line-height: 20px;
}

// ======= 中間：中國段流程 =======
.purchase-flow-guide__center {
  background: rgba(255, 245, 237, 0.6);
  border-radius: 12px;
  display: flex;
  flex: 1;
  flex-direction: column;
  gap: 0;
  margin: 0 15px 0 12px;
  min-width: 0;
  overflow: hidden;
  position: relative;
}

.purchase-flow-guide__center-header {
  align-items: center;
  background: #ffe8d4;
  display: flex;
  justify-content: center;
  padding: 8px 0;
}

.purchase-flow-guide__center-header-left {
  align-items: center;
  display: flex;
  gap: 8px;
}

.purchase-flow-guide__center-flag {
  align-items: center;
  border: 1px solid #fff;
  border-radius: 50%;
  display: flex;
  height: 16px;
  justify-content: center;
  overflow: hidden;
  width: 16px;
}

.purchase-flow-guide__center-header-title {
  color: #9d240f;
  font-size: 16px;
  font-weight: 600;
  line-height: 24px;
}

.purchase-flow-guide__center-header-divider {
  background: #9d240f;
  height: 12px;
  width: 1px;
}

.purchase-flow-guide__center-header-step {
  color: #9d240f;
  font-size: 14px;
  line-height: 20px;
}

// 步驟卡片區
.purchase-flow-guide__center-steps {
  align-items: flex-start;
  display: flex;
  gap: 8px;
  justify-content: center;
  padding: 60px 8px 12px;
}

.purchase-flow-guide__step-card {
  align-items: center;
  background: #fff;
  border: 1px solid #ffcea8;
  border-radius: 12px;
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  // flex-shrink: 0;
  gap: 8px;
  min-height: 158px;
  padding: 20px 12px 12px;
  position: relative;
  width: 142px;
}

.purchase-flow-guide__step-card--green {
  border-color: #bbf7d1;
}

.purchase-flow-guide__step-badge {
  align-items: center;
  background: #ff7228;
  border-radius: 50%;
  color: #fff;
  display: flex;
  font-size: 18px;
  font-weight: 600;
  height: 24px;
  justify-content: center;
  left: 59px;
  position: absolute;
  top: -15px;
  width: 24px;
}

.purchase-flow-guide__step-badge--green {
  background: #15803c;
}

.purchase-flow-guide__step-icon {
  align-items: center;
  background: #fff5ed;
  border-radius: 50%;
  display: flex;
  height: 58px;
  justify-content: center;
  width: 58px;
}

.purchase-flow-guide__step-icon--green {
  background: #f0fdf5;
}

.purchase-flow-guide__step-info {
  display: flex;
  flex-direction: column;
  gap: 4px;
  text-align: center;
}

.purchase-flow-guide__step-title {
  color: #1a1817;
  font-size: 14px;
  font-weight: 600;
  line-height: 20px;
  margin: 0;
}

.purchase-flow-guide__step-desc {
  color: #645a56;
  font-size: 12px;
  line-height: 18px;
  margin: 0;
}

.purchase-flow-guide__step-arrow {
  align-items: center;
  display: flex;
  height: 16px;
  margin-top: 91px;
  flex-shrink: 0;
}

// 底部功能區
.purchase-flow-guide__center-bottom {
  align-items: center;
  background: #fff;
  border-radius: 12px;
  display: flex;
  gap: 12px;
  justify-content: space-between;
  margin: 8px;
  margin-top: auto;
  padding: 11px 12px;
}

.purchase-flow-guide__features {
  display: flex;
  gap: 12px;
}

.purchase-flow-guide__feature-item {
  align-items: center;
  display: flex;
  gap: 8px;
}

.purchase-flow-guide__feature-icon {
  align-items: center;
  background: #fff;
  border-radius: 50%;
  display: flex;
  flex-shrink: 0;
  height: 48px;
  justify-content: center;
  width: 48px;
}

.purchase-flow-guide__feature-text {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.purchase-flow-guide__feature-name {
  color: #1a1817;
  font-size: 13px;
  font-weight: 500;
  line-height: 20px;
  margin: 0;
}

.purchase-flow-guide__feature-desc {
  color: #645a56;
  font-size: 12px;
  line-height: 18px;
  margin: 0;
}

.purchase-flow-guide__detail-btn {
  align-items: center;
  background: #ff7228;
  border: none;
  border-radius: 999px;
  color: #fff;
  cursor: pointer;
  display: flex;
  flex-shrink: 0;
  font-size: 12px;
  font-weight: 500;
  gap: 4px;
  line-height: 16px;
  padding: 8px 12px;
}

.purchase-flow-guide__detail-btn:hover {
  background: #e65d1e;
}

// ======= 右側：國際配送 =======
.purchase-flow-guide__right {
  background: #f0fdf5;
  border-radius: 12px;
  display: flex;
  flex-direction: column;
  flex-shrink: 0;
  overflow: hidden;
  position: relative;
  width: 158px;
}

.purchase-flow-guide__right-header {
  align-items: center;
  background: #dcfce8;
  display: flex;
  justify-content: center;
  padding: 8px 0;
}

.purchase-flow-guide__right-header-left {
  align-items: center;
  display: flex;
  gap: 4px;
}

.purchase-flow-guide__right-globe {
  align-items: center;
  display: flex;
  height: 16px;
  justify-content: center;
  width: 16px;
}

.purchase-flow-guide__right-header-title {
  color: #15803c;
  font-size: 16px;
  font-weight: 600;
  line-height: 24px;
}

.purchase-flow-guide__right-header-divider {
  background: #15803c;
  height: 12px;
  width: 1px;
}

.purchase-flow-guide__right-header-step {
  color: #15803c;
  font-size: 14px;
  line-height: 20px;
}

.purchase-flow-guide__right-step {
  display: flex;
  justify-content: center;
  padding: 60px 8px 12px;
}


// ======= 幫助中心常見問題 (Figma design) =======
.purchase-flow-guide__help {
  background: #fff;
  border-radius: 12px;
  display: flex;
  flex-direction: column;
  gap: 30px;
  padding: 18px 20px;
}

.purchase-flow-guide__help-column {
  display: flex;
  flex: 1;
  flex-direction: column;
  gap: 10px;
  min-width: 0;
}

.purchase-flow-guide__help-title {
  color: #1a1817;
  font-size: 16px;
  font-weight: 600;
  line-height: 24px;
  margin: 0;
}

.purchase-flow-guide__help-links {
  display: flex;
  flex-direction: column;
  gap: 0;
}

.purchase-flow-guide__help-text {
  color: #908380;
  font-size: 14px;
  font-weight: 400;
  line-height: 22px;
  margin: 0;
  padding: 3px 0 3px 12px;
}

// 響應式處理
@media screen and (max-width: 1200px) {
  .purchase-flow-guide__main {
    flex-wrap: wrap;
  }

  .purchase-flow-guide__left {
    width: 100%;
  }

  .purchase-flow-guide__center {
    flex: none;
    margin: 12px 0;
    width: 100%;
  }

  .purchase-flow-guide__right {
    flex-direction: row;
    width: 100%;
  }
}

@media screen and (max-width: 768px) {
  .purchase-flow-guide__restricted {
    height: 120px;
  }

  .purchase-flow-guide__restricted-text {
    font-size: 24px;
    line-height: 32px;
  }

  .purchase-flow-guide__restricted-bg {
    height: 100px;
    width: calc(100% - 40px);
  }

  .purchase-flow-guide__main {
    padding: 12px;
  }

  .purchase-flow-guide__left-header {
    position: relative;
    left: 0;
    transform: none;
    width: 100%;
    border-radius: 12px 12px 0 0;
  }

  .purchase-flow-guide__left-body {
    padding: 16px;
  }

  .purchase-flow-guide__center-steps {
    flex-wrap: wrap;
  }

  .purchase-flow-guide__step-arrow {
    display: none;
  }

  .purchase-flow-guide__features {
    flex-wrap: wrap;
  }

  .purchase-flow-guide__center-bottom {
    flex-wrap: wrap;
  }

  .purchase-flow-guide__right-step {
    padding: 16px;
  }
}
</style>
